import {
  Dispatch,
  FC,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState
} from 'react';

interface ResponseContextType {
  error: string;
  clearError: () => void;
  handleError: (e: unknown, json?: any) => Promise<void>;
  setError: Dispatch<SetStateAction<string>>;
  success: ReactNode;
  setSuccess: Dispatch<SetStateAction<ReactNode>>;
}
const ResponseContext = createContext<ResponseContextType>({
  error: '',
  clearError: () => {},
  handleError: async () => {},
  setError: () => {},
  success: null,
  setSuccess: () => {}
});
export const useResponse = () => useContext(ResponseContext);

export const ResponseProvider: FC<PropsWithChildren> = ({ children }) => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<ReactNode>(null);

  const clearError = useCallback(() => setError(''), [setError]);

  /* Error | Response | string | unknown */
  const handleError = useCallback(
    async (e: unknown, json?: any) => {
      if (e instanceof Error) {
        setError(e.message || 'Error');
      } else if (e instanceof Response) {
        switch (e.status) {
          case 500:
            setError('INTERNAL SERVER ERROR');
            break;
          case 403:
            setError('FORBIDDEN');
            break;
          case 401:
            setError('UNAUTHORIZED');
            break;
          default:
            const awaitedJson = json ?? (await e.json());
            setError(awaitedJson.message?.match(/"[^"]+"/)?.[0] ?? e.statusText);
        }
      } else if (typeof e === 'string') {
        setError(e || 'Error');
      } else {
        setError('Error');
      }
    },
    [setError]
  );

  return (
    <ResponseContext.Provider value={{ error, success, clearError, handleError, setError, setSuccess }}>
      {children}
    </ResponseContext.Provider>
  );
};
